import { useNavigate } from "react-router-dom";
import { useCallback } from "react";

export const useScrollToMessage = ({
  messageRefs,
  messagesContainerRef,
  isProcessingUrlTargetRef,
  previousMessageThemeRef,
  urlTargetMessageId,
  setWaitForAnimation,
  setIsSearchingMessage,
  updateMessageTheme,
  cleanMessageArray,
  fetchInitialMessages,
  messageNotFoundRef,
  messages,
}: {
  messageRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
  messagesContainerRef: React.MutableRefObject<HTMLDivElement | null>;
  isProcessingUrlTargetRef: React.MutableRefObject<boolean>;
  previousMessageThemeRef: React.MutableRefObject<string | null>;
  urlTargetMessageId: string | null;
  setWaitForAnimation: (value: boolean) => void;
  setIsSearchingMessage: (value: boolean) => void;
  updateMessageTheme: (
    newMessage: "both" | "lessons" | "chats",
  ) => Promise<string>;
  cleanMessageArray: () => Promise<void>;
  fetchInitialMessages: (messageId?: string) => Promise<any>;
  messageNotFoundRef: React.MutableRefObject<boolean>;
  messages: any[];
}) => {
  const navigate = useNavigate();
  let retryWithThemeChangeCount = 0;
  const maxThemeChangeRetries = 3;

  const scrollToMessageWithRetry = useCallback(
    (messageId: string, isFromUrl = false) => {
      setWaitForAnimation(true);

      if (messageNotFoundRef) {
        messageNotFoundRef.current = false;
      }

      let retryCount = 0;
      const maxRetries = 10;

      const attemptScroll = () => {
        retryCount++;

        if (retryCount === 3) {
          setIsSearchingMessage(true);
        }

        const messageExistsInData =
          messages && messages.some((m) => m.message_id === messageId);

        const targetElement = messageRefs.current[messageId];
        const container = messagesContainerRef.current;

        const retryWithThemeChange = async () => {
          try {
            if (retryWithThemeChangeCount >= maxThemeChangeRetries) {
              return;
            }
            retryWithThemeChangeCount++;
            
            setIsSearchingMessage(true);

            await updateMessageTheme("both");
            await cleanMessageArray();
            const result = await fetchInitialMessages(messageId);
            previousMessageThemeRef.current = "both";

            const messageInFetchedResults =
              Array.isArray(result) &&
              result.some((msg) => msg.message_id === messageId);

            if (!messageInFetchedResults && messageNotFoundRef) {
              messageNotFoundRef.current = true;
              setWaitForAnimation(false);
              setIsSearchingMessage(false);

              if (isFromUrl) {
                navigate(window.location.pathname, { replace: true });
                isProcessingUrlTargetRef.current = false;
              }

              return;
            }

            setTimeout(() => {
              scrollToMessageWithRetry(messageId, isFromUrl);
            }, 500);
          } catch (error) {
            if (messageNotFoundRef) {
              messageNotFoundRef.current = true;
            }

            setWaitForAnimation(false);
            setIsSearchingMessage(false);

            if (isFromUrl) {
              navigate(window.location.pathname, { replace: true });
              isProcessingUrlTargetRef.current = false;
            }

            if (messagesContainerRef.current) {
              messagesContainerRef.current.scrollTop =
                messagesContainerRef.current.scrollHeight;
            }
          }
        };

        if (targetElement && container) {
          requestAnimationFrame(() => {
            const { top: elementTopRelative } =
              targetElement.getBoundingClientRect();
            const { top: containerTopRelative } =
              container.getBoundingClientRect();

            const elementTop =
              elementTopRelative + container.scrollTop - containerTopRelative;
            const containerHeight = container.clientHeight;
            const elementHeight = targetElement.offsetHeight;

            const targetScrollTop = Math.max(
              0,
              elementTop - (containerHeight - elementHeight) / 2,
            );

            container.scrollTo({
              top: targetScrollTop,
              behavior: "auto",
            });

            setTimeout(() => {
              const afterScrollRect = targetElement.getBoundingClientRect();
              const containerRect = container.getBoundingClientRect();

              const elementCenter =
                afterScrollRect.top + afterScrollRect.height / 2;
              const containerCenter =
                containerRect.top + containerRect.height / 2;
              const centerDiff = elementCenter - containerCenter;

              if (Math.abs(centerDiff) > 20) {
                const adjustment = centerDiff;

                container.scrollBy({
                  top: adjustment,
                  behavior: "smooth",
                });
              }

              if (targetElement.classList.contains("highlight-message")) {
                targetElement.classList.remove("highlight-message");
              }
              targetElement.classList.add("highlight-message");

              setTimeout(() => {
                if (targetElement) {
                  targetElement.classList.remove("highlight-message");
                }
              }, 2000);

              setWaitForAnimation(false);
              setIsSearchingMessage(false);

              if (isFromUrl) {
                navigate(window.location.pathname, { replace: true });
                isProcessingUrlTargetRef.current = false;
              }
            }, 300);
          });

          return;
        } else if (retryCount >= maxRetries) {
          if (messageExistsInData) {
            setTimeout(() => {
              const finalCheck = messageRefs.current[messageId];
              if (finalCheck) {
                attemptScroll();
                return;
              }

              
              retryWithThemeChange();
            }, 1000);
            return;
          }

          retryWithThemeChange();
          return;
        } else {
          setTimeout(attemptScroll, 100 * retryCount);
        }
      };

      attemptScroll();
    },
    [
      messageRefs,
      messagesContainerRef,
      setWaitForAnimation,
      setIsSearchingMessage,
      updateMessageTheme,
      cleanMessageArray,
      fetchInitialMessages,
      navigate,
      isProcessingUrlTargetRef,
      previousMessageThemeRef,
      messageNotFoundRef,
    ],
  );

  const handleScrollToMessage = useCallback(
    (id: string) => {
      const fromUrl = id === urlTargetMessageId && urlTargetMessageId !== null;
      scrollToMessageWithRetry(id, fromUrl);
    },
    [scrollToMessageWithRetry, urlTargetMessageId],
  );

  return {
    scrollToMessageWithRetry,
    handleScrollToMessage,
  };
};
